import React, { useMemo } from 'react';

import { makeStyles, Theme } from '@material-ui/core/styles';
import { TranslateFunction } from 'react-localize-redux';

import { Voucher } from '../../../core/state/store/store.types';
import { Button, Typography } from '../../atoms';
import { List, ListItem } from '../../atoms/List';
import { Dialog } from '../../layouts';

type Props = {
  isDialogOpen: boolean;
  onApplyVoucher: (voucherCode: string) => void;
  onCloseDialog: () => void;
  onShowVoucherDialog: (voucher) => void;
  translate: TranslateFunction;
  vouchers?: Voucher[];
};

const useStyles = makeStyles((theme: Theme) => ({
  buttonGroup: {
    paddingTop: theme.spacing(1.5),

    '& button:nth-child(2)': {
      marginLeft: theme.spacing(1.5),
    },
  },
  description: {
    marginTop: theme.spacing(0.5),
    marginBottom: theme.spacing(1.5),
  },
  dialogContent: {
    minHeight: '500px',
    paddingTop: theme.spacing(3),
  },
  voucherOption: {
    alignItems: 'center',
    display: 'flex',
    minHeight: '56px',
  },
}));

export function LoyaltyVoucherListDialog(props: Props): JSX.Element {
  const classes = useStyles();
  const {
    isDialogOpen,
    onApplyVoucher,
    onCloseDialog,
    onShowVoucherDialog,
    translate,
    vouchers = [],
  } = props;

  const renderVoucher = (voucher: Voucher): JSX.Element => {
    const validUntilText = translate('LoyaltyVouchers_validUntil', {
      expiryDate: voucher.expiryDate,
    }) as string;
    return (
      <div>
        <Typography variant="h3">
          {translate('Loyalty_loyaltyRewardTitle')}
        </Typography>
        <div className={classes.description}>
          <Typography variant="body2-secondary">
            {translate('Loyalty_loyaltyRewardSubTitle', {
              voucherCode: voucher.code,
              percentage: voucher.discountValue,
            })}
          </Typography>
        </div>

        <Typography variant="body2-secondary">
          {validUntilText.toUpperCase()}
        </Typography>

        <div className={classes.buttonGroup}>
          <Button onClick={() => onApplyVoucher(voucher.code)} size="small">
            {translate('LoyaltyPopout_apply')}
          </Button>
          <Button
            onClick={() => onShowVoucherDialog(voucher)}
            size="small"
            variant="secondary"
          >
            {translate('LoyaltyPopout_findOutMore')}
          </Button>
        </div>
      </div>
    );
  };

  const renderVoucherListItems = useMemo(() => {
    const optionsMapped: ListItem[] = vouchers.map((option) => ({
      key: option.code,
      primaryContent: renderVoucher(option),
    }));

    return optionsMapped;
  }, [vouchers.length]);

  return (
    <Dialog
      dialogName="loyalty-voucher-list-dialog"
      onTitleClick={(): void => onCloseDialog()}
      open={isDialogOpen}
    >
      <Typography variant="h1">
        {translate('LoyaltyVouchersDialog_title')}
      </Typography>
      <div className={classes.dialogContent}>
        <List items={renderVoucherListItems} />
      </div>
    </Dialog>
  );
}
